/**
 * stuburt-gift-keychain.js — <stuburt-gift-keychain>
 *
 * Gift-with-purchase automation for the Stuburt × Ricci Mandal "Play Well" drop:
 * once the bag holds N qualifying Play Well items, the co-branded keychain is
 * added for the customer, and it is taken back out if the bag drops below N.
 *
 * Shopify cannot do this on its own. An automatic "Buy X get Y" discount can
 * make the keychain free, but it never PUTS it in the cart — the customer has
 * to add it. So the two halves are:
 *
 *   1. the discount (admin)  — makes the line £0
 *   2. this element (theme)  — puts the line there, and takes it away again
 *
 * Because half the mechanic lives in the admin, the element refuses to leave a
 * chargeable line behind: after every add it re-reads the cart, and if the
 * keychain is NOT fully discounted it removes it again and switches itself off
 * for the session. A missing or expired discount degrades to "no gift", never
 * to "charged for something they didn't pick".
 *
 * Markup + config: snippets/stuburt-gift-keychain.liquid (mounted once in
 * layout/theme.liquid, so it works on every page, not just the cart).
 *
 * Cart state is read from /cart.js rather than tracked client-side — the source
 * of truth is always Shopify. No money is computed here; the only numbers this
 * file touches are item counts and a zero check.
 *
 * Custom Element; all listeners bound to an AbortController and torn down in
 * disconnectedCallback.
 */

/** Session keys. Per-tab-session on purpose: a removal should be respected for
 *  the visit, not for ever. */
const KEY_ADDED = 'stuburt:gift:added';
const KEY_DISMISSED = 'stuburt:gift:dismissed';
const KEY_BLOCKED = 'stuburt:gift:blocked';

/** Line property that marks a keychain WE added, so a keychain the customer
 *  chose themselves is never silently removed. Leading underscore keeps it out
 *  of the cart line's visible properties (see snippets/cart-products.liquid). */
const GIFT_PROPERTY = '_playwell_gift';

/** Our own cart:update source, so the reconcile loop ignores its own echo. */
const SOURCE = 'stuburt-gift-keychain';

class StuburtGiftKeychain extends HTMLElement {
  #abort = null;
  #config = null;
  #busy = false;
  #pending = false;
  #timer = 0;

  connectedCallback() {
    const variantId = this.dataset.giftVariant;
    const giftProductId = this.dataset.giftProduct;
    const qualifying = (this.dataset.qualifying || '').split(',').filter(Boolean);
    const threshold = parseInt(this.dataset.threshold, 10);

    // The snippet only renders when all of this is present, but a half-set-up
    // gift (draft product, empty collection) must be inert rather than noisy.
    if (!variantId || !giftProductId || qualifying.length === 0 || !threshold) return;

    this.#config = { variantId, giftProductId, qualifying: new Set(qualifying), threshold };

    this.#abort = new AbortController();
    document.addEventListener('cart:update', this.#onCartUpdate, { signal: this.#abort.signal });

    // Reconcile once on load too: the qualifying add may have happened on a
    // previous page view, or the cart may have been changed in another tab.
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    clearTimeout(this.#timer);
    this.#busy = false;
    this.#pending = false;
  }

  #onCartUpdate = (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    this.#schedule();
  };

  /** Coalesce the burst of events a single cart change can fire. */
  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#reconcile(), 150);
  }

  async #reconcile() {
    if (this.#read(KEY_BLOCKED)) return;
    // A change that lands mid-flight must not be dropped, or the cart can settle
    // out of step with the offer. Queue one re-run instead.
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;

    try {
      const cart = await this.#cart();
      if (!cart) return;

      const { qualifying, giftProductId, threshold } = this.#config;
      let count = 0;
      let giftLine = null;
      let chosenLine = null;

      for (const item of cart.items || []) {
        const productId = String(item.product_id);
        if (qualifying.has(productId)) count += item.quantity;
        if (productId !== giftProductId) continue;
        if (item.properties?.[GIFT_PROPERTY]) giftLine = item;
        else chosenLine = item;
      }

      if (count >= threshold) {
        // Already there — just make sure a nudged quantity can't ride along,
        // since the discount only frees one.
        if (giftLine) {
          if (giftLine.quantity > 1) await this.#change(giftLine.key, 1);
          return;
        }
        // They put a keychain in the bag themselves; the discount covers it.
        if (chosenLine) return;
        if (this.#read(KEY_DISMISSED)) return;
        // We added it and it is gone while they still qualify — they took it
        // out. Respect that for the rest of the visit instead of fighting them.
        if (this.#read(KEY_ADDED)) {
          this.#write(KEY_DISMISSED, '1');
          this.#clear(KEY_ADDED);
          return;
        }
        await this.#add();
      } else if (giftLine) {
        await this.#change(giftLine.key, 0);
        this.#clear(KEY_ADDED);
      }
    } catch {
      // A failed gift add is never worth breaking the cart over — the next
      // cart:update will try again.
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        this.#schedule();
      }
    }
  }

  async #cart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return null;
    return res.json();
  }

  async #add() {
    const body = new FormData();
    body.set('id', this.#config.variantId);
    body.set('quantity', '1');
    body.set(`properties[${GIFT_PROPERTY}]`, 'true');

    // No X-Requested-With: the store's bot protection answers cart adds carrying
    // AJAX markers with a fake 422 "sold out" (see error ledger 2026-07-05).
    const res = await fetch('/cart/add.js', { method: 'POST', body, headers: { Accept: 'application/json' } });
    if (!res.ok) return;

    this.#write(KEY_ADDED, '1');

    // The safety net. Automatic discounts apply to the cart, not just checkout,
    // so a correctly configured "Buy X get Y" leaves this line at zero. Anything
    // else means the discount is missing, expired or not combining — back the
    // line out and stand down rather than charge for an unrequested item.
    const cart = await this.#cart();
    const line = cart?.items?.find((item) => item.properties?.[GIFT_PROPERTY]);

    if (line && line.final_line_price > 0) {
      this.#write(KEY_BLOCKED, '1');
      this.#clear(KEY_ADDED);
      await this.#change(line.key, 0);
      console.warn('[stuburt-gift] keychain was not free in the cart — gift automation stood down for this session');
      return;
    }

    this.#notify(cart);
  }

  async #change(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity }),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!res.ok) return;
    this.#notify(await res.json());
  }

  /** Tell the cart icon, drawer and cart page to re-render server-side. */
  #notify(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            // Not 'product-form-component': that would make the bubble ADD to
            // its count and pop the mini-cart. An absolute count is what we want.
            source: SOURCE,
            itemCount: cart?.item_count ?? 0,
            quiet: true,
          },
        },
      })
    );
  }

  #read(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  #write(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* private mode — the gift just won't be sticky */
    }
  }

  #clear(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* no-op */
    }
  }
}

if (!customElements.get('stuburt-gift-keychain')) {
  customElements.define('stuburt-gift-keychain', StuburtGiftKeychain);
}
